import { jsxs, Fragment, jsx } from "react/jsx-runtime";
import React__default, { createContext, useRef, useMemo, useEffect, useContext, useCallback } from "react";
import { createRoot } from "react-dom/client";
import { getMarketPrefix } from "../../config.js";
import { useNamespacedMarketComponents } from "../../utils/Market/index.js";
const ToastContext = createContext({
  getToaster: () => null
});
function ToastProvider({
  children,
  applyHighZIndex = false
}) {
  const toasterRef = useRef(null);
  const { MarketToaster } = useNamespacedMarketComponents();
  const toasterContext = useMemo(
    () => ({
      // Do not read ref during render cycle, instead expose a getter
      getToaster: () => toasterRef.current
    }),
    [toasterRef]
  );
  useEffect(() => {
    if (applyHighZIndex && toasterRef.current) {
      toasterRef.current.style.zIndex = "9999";
    }
  }, [applyHighZIndex]);
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(MarketToaster, { ref: toasterRef }),
    /* @__PURE__ */ jsx(ToastContext.Provider, { value: toasterContext, children })
  ] });
}
const createMarketToastElement = ({
  persistent = false,
  text,
  variant = "info",
  actions
}) => {
  const toastElement = document.createElement(
    `${getMarketPrefix()}market-toast`
  );
  toastElement.persistent = persistent;
  toastElement.variant = variant;
  if (actions) {
    const slottedActions = actions.map((action, index) => React__default.cloneElement(action, {
      key: action.key ?? `action-${index}`,
      slot: "action"
    }));
    createRoot(toastElement).render(
      /* @__PURE__ */ jsxs(Fragment, { children: [
        text,
        slottedActions
      ] })
    );
  } else {
    toastElement.textContent = text;
  }
  return toastElement;
};
const useToaster = () => {
  const { getToaster } = useContext(ToastContext);
  const showToast = useCallback(
    async (toast) => {
      const marketToaster = getToaster();
      if (!marketToaster) {
        return null;
      }
      const marketToastElement = createMarketToastElement(toast);
      await marketToaster.show(marketToastElement);
      return marketToastElement;
    },
    [getToaster]
  );
  const removeAll = useCallback(
    () => {
      const marketToaster = getToaster();
      if (!marketToaster) {
        return Promise.reject(new Error("MarketToaster not found"));
      }
      return marketToaster.removeAll();
    },
    [getToaster]
  );
  const hide = useCallback(
    (...options) => {
      const [toastEl] = options;
      if (!toastEl) {
        return;
      }
      const marketToaster = getToaster();
      if (!marketToaster) {
        return Promise.reject(new Error("MarketToaster not found"));
      }
      return marketToaster.hide(...options);
    },
    [getToaster]
  );
  return useMemo(
    () => ({
      showToast,
      removeAll,
      hide
    }),
    [showToast, removeAll, hide]
  );
};
export {
  ToastContext,
  ToastProvider,
  useToaster
};
//# sourceMappingURL=ToastProvider.js.map
